import classnames from 'classnames';
import moment from 'moment';
import ToolTip from 'components/common/tool-tip-component';
import { EMPTY_STRING } from 'constants/common/feature-common-utils';
import { getUserNameByUserId } from 'utils/statement-content-page-utils';
import TabModel from 'models/utils/common/tab-model';
import { MenuOption } from 'models/utils/common/menu/menu-option-model';
import { RadioOption } from 'models/utils/common/radio-options-model';
import DataGridColumn from 'models/utils/common/data-grid/data-grid-column-model';
import MultilineEllipsis from 'components/common/multi-line-ellipsis-text-component';
import LinkedStatementCancelIcon from 'pages/side-by-side-compare/left-side/linked-statement-cancel-icon';
import {
  AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
  AUTO_TIEOUT_COMPARE_MODE_NEW,
} from 'constants/feature/statement-content-constants';
import { ReactComponent as CompletedIcon } from 'icons/complete.svg';
import { ReactComponent as InfoIcon } from 'icons/info.svg';
import { ReactComponent as InProgressIcon } from 'icons/in-progress.svg';
import { ReactComponent as FailedIcon } from 'icons/error-diamond.svg';
import { ReactComponent as ChainIcon } from 'icons/chain.svg';
import { isNullOrUndefined } from 'utils/object-utils';
import { getElementResultByNumberFormat } from 'utils/formula-utils';
import { ReactComponent as SquareIcon } from 'icons/square.svg';
import { ReactComponent as TriangleIcon } from 'icons/triangle.svg';
import { ELEMENT_HIGHLIGHT_STATES } from 'constants/feature/tieout-element-constants';
const ICON_SIZE = 10;
const INFO_ICON_SIZE = 20;

export const getTrialBalanceTabIcons = ({ BLOCK }) => {
  return new Map([
    [
      TRIAL_BALANCE_TABS.balanceSheet.id,
      {
        iconComponent: <SquareIcon width={ICON_SIZE} height={ICON_SIZE} />,
        className: `${BLOCK}__trial-balance-tab-icon`,
      },
    ],
    [
      TRIAL_BALANCE_TABS.incomeStatement.id,
      {
        iconComponent: <TriangleIcon width={ICON_SIZE} height={ICON_SIZE} />,
        className: `${BLOCK}__trial-balance-tab-icon`,
      },
    ],
  ]);
};

const AUTO_TIEOUT_STATUS_TO_ICON_MAP = {
  IN_PROGRESS: {
    icon: <InProgressIcon />,
    className: 'in-progress-icon',
    tooltipText:
      'side-by-side-view.compare.linked-statements.status.inprogress.tooltip.message',
  },
  NEW: {
    icon: <InProgressIcon />,
    className: 'in-progress-icon',
    tooltipText:
      'side-by-side-view.compare.linked-statements.status.inprogress.tooltip.message',
  },
  COMPLETED: {
    icon: <CompletedIcon />,
    className: 'completed-icon',
    tooltipText:
      'side-by-side-view.compare.linked-statements.status.completed.tooltip.message',
  },
  FAILED: {
    icon: <FailedIcon />,
    className: 'failed-icon',
    tooltipText:
      'side-by-side-view.compare.linked-statements.status.failed.tooltip.message',
  },
  CANCELLED: {
    icon: <FailedIcon />,
    className: 'failed-icon',
    tooltipText:
      'side-by-side-view.compare.linked-statements.status.failed.tooltip.message',
  },
};

export const AUTO_TIEOUT_BANNER_MESSAGES = {
  INPROGRESS: 'side-by-side-view.compare.auto-tieout-inprogress.description',
  SUCCESS: 'side-by-side-view.compare.auto-tieout-success.description',
  FAILURE_AUTO_TIEOUT_TO_ANOTHER_STATEMENT:
    'side-by-side-view.compare.auto-tieout-failure.description.tieout-to-another-statement',
  FAILURE_TRIAL_BALANCE:
    'side-by-side-view.compare.auto-tieout-failure.description.trial-balance',
  MAX_RETRIES:
    'side-by-side-view.compare.auto-tieout-failure-max-retries.description',
};

export const getProjectNameMenuOptions = (projectList) => {
  return projectList.map(
    (project) =>
      new MenuOption({
        id: project.id,
        title: project.name,
        value: project,
        isIntl: false,
      }),
  );
};

export const getStatementNameMenuOptions = (
  statementList,
  linkedStatementsMap,
) => {
  return statementList.map(
    (statement) =>
      new RadioOption({
        id: statement.id,
        label: `${statement.statementName}${getTagForStatementName(statement)}`,
        value: statement.id,
        isNotInternationalized: true,
        disabled: linkedStatementsMap.has(statement.revisionId),
        tooltipEnabled: linkedStatementsMap.has(statement.revisionId),
      }),
  );
};

export const getTagForStatementName = (statement) => {
  if (statement.isSoftDeleted()) return ' (Deleted)';
  else if (statement.isWrappedUp()) return ' (Wrapped up)';
  else if (statement.isPendingApproval()) return ' (Approval pending)';
  else if (statement.hasApprovalInitiated()) return ' (Wrap-up in progress)';
  else return EMPTY_STRING;
};

export const dropdownOptionsForRevision = [
  {
    label: 'side-by-side-view.compare.dropdown.workpaper.label',
    name: 'side-by-side-view.compare.dropdown.workpaper.name',
    checked: true,
    id: 'wp',
  },
  {
    label: 'side-by-side-view.compare.dropdown.tickmarks.label',
    name: 'side-by-side-view.compare.dropdown.tickmarks.name',
    checked: true,
    id: 'tm',
  },
  {
    label: 'side-by-side-view.compare.dropdown.verification-status.label',
    name: 'side-by-side-view.compare.dropdown.verification-status.name',
    checked: true,
    id: 'vs',
  },
];

export const dropdownOptionsForStatements = [
  {
    label: 'side-by-side-view.compare.dropdown.workpaper.label',
    name: 'side-by-side-view.compare.dropdown.workpaper.name',
    checked: true,
    id: 'wp',
  },
  {
    label: 'side-by-side-view.compare.dropdown.tickmarks.label',
    name: 'side-by-side-view.compare.dropdown.tickmarks.name',
    checked: true,
    id: 'tm',
  },
  {
    label: 'side-by-side-view.compare.dropdown.py.label',
    name: 'side-by-side-view.compare.dropdown.py.label',
    checked: true,
    id: 'py',
  },
  {
    label: 'side-by-side-view.compare.dropdown.pq.label',
    name: 'side-by-side-view.compare.dropdown.pq.label',
    checked: true,
    id: 'pq',
  },
];

export const TAB_NAME = {
  LEFT: 'left',
  RIGHT: 'right',
};

export const getLinkedStatementColumns = (
  onUnlinkIconClick,
  onCancelIconClick,
  isDisableUnlink,
  isCurrentStatementWrappedUp,
  blockName,
  currentUserId,
  projectUsersList,
) => {
  let linkedStatementColumns = [
    new DataGridColumn({
      key: 'clientName',
      header: 'auto-tieout.linked-statements.table.column.project',
      width: '30%',
      formatter: (rowData, { rowKey }) => {
        const colId = `linked-statement-client-col-${rowKey}`;
        return (
          <MultilineEllipsis
            toolTipID={colId}
            text={rowData.clientName}
            isNotInternationalized
            tooltipPosition="bottom"
          />
        );
      },
    }),
    new DataGridColumn({
      key: 'statement',
      header: 'auto-tieout.linked-statements.table.column.statement',
      width: '45%',
      formatter: (rowData, { rowKey }) => {
        const colId = `linked-statement-statement-col-${rowKey}`;
        const statementName = rowData.isStatementNameNotFound
          ? 'Statement is permanently deleted'
          : rowData.statementName;
        const statementNameWithTag = rowData.isStatementDeleted
          ? `Deleted ${statementName}`
          : statementName;

        const statusIcon =
          AUTO_TIEOUT_STATUS_TO_ICON_MAP[rowData.workflowStatus]?.icon;
        const iconClassName = classnames(
          `${blockName}__statement-cell-status`,
          AUTO_TIEOUT_STATUS_TO_ICON_MAP[rowData.workflowStatus]?.className,
          {
            ['icon-disabled']:
              rowData.isStatementDeleted || rowData.isStatementStatusNotFound,
          },
        );

        return (
          <div className={`${blockName}__statement-cell`}>
            <div className={`${blockName}__statement-cell-text`}>
              <MultilineEllipsis
                toolTipID={colId}
                text={statementNameWithTag}
                isNotInternationalized
                tooltipPosition="bottom"
              />
            </div>
            {rowData.isStatementDeleted || rowData.isStatementStatusNotFound ? (
              <div className={iconClassName}>{statusIcon}</div>
            ) : (
              <div className={iconClassName}>
                <ToolTip
                  id={`${colId}-status`}
                  text={
                    AUTO_TIEOUT_STATUS_TO_ICON_MAP[rowData.workflowStatus]
                      ?.tooltipText
                  }
                  position="bottom"
                >
                  {statusIcon}
                </ToolTip>
              </div>
            )}
          </div>
        );
      },
    }),
    new DataGridColumn({
      key: 'workpaper',
      header: 'auto-tieout.linked-statements.table.column.workpaper',
      width: '15%',
      formatter: (rowData, { rowKey }) => {
        const colId = `linked-statement-workpaper-col-${rowKey}`;
        return (
          <MultilineEllipsis
            toolTipID={colId}
            text={rowData.referenceNumber}
            isNotInternationalized
            tooltipPosition="bottom"
          />
        );
      },
    }),
    new DataGridColumn({
      key: 'infoIcon',
      width: '5%',
      formatter: (rowData, { rowKey }) => {
        const colId = `linked-statement-info-col-${rowKey}`;

        if (rowData.processedDate) {
          return (
            <div className={`${blockName}__info-icon`}>
              <ToolTip
                id={colId}
                text={{
                  id: 'side-by-side-view.compare.linked-statements.last-auto-tieout-info.tooltip.message',
                  values: {
                    practitionerName: getUserNameByUserId(
                      rowData.initiatorId,
                      projectUsersList,
                    ),
                    date: moment
                      .unix(rowData.processedDate)
                      .format('MM/DD/YYYY'),
                  },
                }}
                tooltipPosition="top"
              >
                <InfoIcon width={INFO_ICON_SIZE} height={INFO_ICON_SIZE} />
              </ToolTip>
            </div>
          );
        }
      },
    }),
    new DataGridColumn({
      key: 'unlinkIcon',
      width: '5%',
      formatter: (rowData, { rowKey }) => {
        const colId = `linked-statement-unlink-col-${rowKey}`;
        const isAutoTieoutInProgress = [
          AUTO_TIEOUT_COMPARE_MODE_INPROGRESS,
          AUTO_TIEOUT_COMPARE_MODE_NEW,
        ].includes(rowData.workflowStatus);

        if (isAutoTieoutInProgress && currentUserId !== rowData.initiatorId) {
          return null;
        }

        const isIconDisabled = isDisableUnlink || isCurrentStatementWrappedUp;

        return (
          <LinkedStatementCancelIcon
            colId={colId}
            isIconDisabled={isIconDisabled}
            isAutoTieoutInProgress={isAutoTieoutInProgress}
            onUnlinkIconClick={onUnlinkIconClick}
            onCancelIconClick={onCancelIconClick}
            rowData={rowData}
            isCurrentStatementWrappedUp={isCurrentStatementWrappedUp}
          />
        );
      },
    }),
  ];
  return linkedStatementColumns;
};

export const getStatementHeader = (leftSelectedStatement, leftRevision) => {
  return (
    leftSelectedStatement &&
    leftSelectedStatement.statement &&
    `${leftSelectedStatement.statement.statementName}  V${leftRevision.revisionNumber}`
  );
};

export const TRIAL_BALANCE_TABS = {
  balanceSheet: new TabModel({
    id: 'statement-nav-balanceSheet-tab',
    intlTitle: 'trial-balance-view.tab.balanceSheet.title',
  }),
  incomeStatement: new TabModel({
    id: 'statement-nav-incomeStatement-tab',
    intlTitle: 'trial-balance-view.tab.incomeStatement.title',
  }),
};

export const TRIAL_BALANCE_TABS_ARRAY = Object.values(TRIAL_BALANCE_TABS);

const currencySymbolsMap = {
  USD: '$',
  EUR: '€',
};

export const formatAmountWithCurrency = ({
  amount,
  currency,
  defaultScaling,
  numberFormatId,
}) => {
  if (isNullOrUndefined(amount)) return '';
  const symbol = currencySymbolsMap[currency] || '';
  const isNegative = amount < 0;
  const formattedAmount = getElementResultByNumberFormat(
    isNegative ? Math.abs(amount) : amount,
    numberFormatId,
    defaultScaling?.value,
  );

  return isNegative
    ? `-${symbol}${formattedAmount}`
    : `${symbol}${formattedAmount}`;
};

export const trialBalanceSnapShotView = {
  //header : column Name for Table
  //id: This id is used to provide the specific width/alignment in tableConfig else default width(auto) alignment(left) will be applied
  //accessor: A key used to access the value in data object and also useful while scrolling to a column in specific row
  getHeaders: ({
    currency,
    mappedElements,
    defaultScaling,
    numberFormatId,
    onAmountClick,
    sideBySideElementMap,
    cellHighlight,
    tbClickedElement,
    highlightMappedElements,
  }) => [
    {
      header: 'Account',
      id: 'account',
      accessor: 'name',
    },
    {
      header: 'Consolidated',
      id: 'consolidated',
      accessor: 'amount',
      customCell: (row) => {
        const { amount, id } = row.original;
        const isMapped = mappedElements?.[id];

        const selectedSourceElement =
          sideBySideElementMap?.getSourceElementMapByElementId(id);
        const selectionCount = selectedSourceElement
          ? sideBySideElementMap.getCountForMappedSourceElement(id)
          : null;

        const highlightColor = cellHighlight?.has?.(id)
          ? cellHighlight.getElementHighlightColor(id)
          : null;

        const isClickedMappedElement = tbClickedElement?.tbDataId == id;

        const highlightClassByToggle = 'data-table__cell--map-highlight';
        const highlightClassByClickOrBySuccess =
          'data-table__cell--map-success';
        const highlightClassByFailure = 'data-table__cell--map-failure';

        const getHighlightClass = () => {
          if (isClickedMappedElement) return highlightClassByClickOrBySuccess;
          if (highlightColor === ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_SUCCESS)
            return highlightClassByClickOrBySuccess;
          if (isMapped && highlightMappedElements)
            return highlightClassByToggle;
          if (highlightColor === ELEMENT_HIGHLIGHT_STATES.ELEMENT_MAP_FAILURE)
            return highlightClassByFailure;
          return '';
        };

        return {
          icon: isMapped ? (
            <ChainIcon width={ICON_SIZE} height={ICON_SIZE} />
          ) : null,
          value: formatAmountWithCurrency({
            amount,
            currency,
            defaultScaling,
            numberFormatId,
          }),
          onClick:
            onAmountClick && amount !== null && amount !== undefined
              ? () => onAmountClick(row.original)
              : null,
          selectionCount,
          isSelected: !!selectedSourceElement,
          highlightClass: getHighlightClass(),
        };
      },
    },
  ],

  scrollConfig: {
    highlightColumn: 'amount',
    onScrollError: (error) => console.error('Scroll error:', error),
    onScrollSuccess: (row) => console.log('Scrolled to:', row.original),
  },

  tableConfig: {
    getSubRows: (row) => row.fslis || row.accounts,
    className: 'trial-balance-snapshot__table',
    columnWidths: {
      expander: '50px',
      consolidated: '200px',
    },
    columnAlignments: {
      expander: 'center',
      consolidated: 'right',
    },
  },
};
